/**
 * The wire protocol. One definition, imported by both sides, so a type error
 * catches protocol drift before a deploy does.
 *
 * The rule that outranks everything else here: a `RoomView` is built PER
 * RECIPIENT on the server. There is no redaction flag and no client-side
 * filtering. If a field is not in your view, the bytes never reached your
 * socket. `engine.viewFor` is where that is actually enforced; this file only
 * describes the envelope around it.
 */

import type { ChipId } from './chips.js';
import type { Answer, Phase, View } from './engine.js';

export interface PublicPlayer {
  id: string;
  name: string;
  online: boolean;
  isHost: boolean;
  isBot: boolean;
}

/** Everything one socket is allowed to know about its room. */
export interface RoomView {
  code: string;
  /** Lobby membership, which exists before a `GameState` does. */
  players: PublicPlayer[];
  /** Null until the host starts. Already redacted for this recipient. */
  game: View | null;
  you: {
    id: string;
    isHost: boolean;
    /** Whether this socket may start or restart the game right now. */
    mayStart: boolean;
  };
  /** Set when the host seat moved on its own, so the UI can say so. */
  hostChangedTo: string | null;
  /** Host-only, and only once the timer has elapsed: someone has dropped. */
  hostAction: { kind: 'kickIdle'; names: string[] } | null;
  phase: Phase | 'lobby';
}

/**
 * Phase-scoped messages carry the state they were composed against.
 *
 * `pendingId` on `answer` is the important one. A phone double-taps, React
 * double-renders, and a reconnect resends — without an id the second copy
 * answers whatever prompt is open by the time it lands, which is exactly how a
 * "return one chip" tap becomes "trash Excalibur".
 */
export type ClientMsg =
  | { t: 'create'; name: string }
  | { t: 'join'; code: string; name: string; token?: string }
  | { t: 'start'; bots: number }
  | { t: 'draw'; region: number }
  | { t: 'play'; chip: ChipId }
  | { t: 'unplay' }
  | { t: 'answer'; pendingId: number; answer: Answer }
  | { t: 'again' }
  | { t: 'kick'; playerId: string }
  | { t: 'ping' };

export type ServerMsg =
  | { t: 'joined'; code: string; playerId: string; token: string; view: RoomView }
  | { t: 'view'; view: RoomView }
  | { t: 'error'; code: ErrorCode; message: string }
  | { t: 'pong' };

export type ErrorCode =
  | 'ROOM_NOT_FOUND'
  | 'ROOM_FULL'
  | 'GAME_IN_PROGRESS'
  | 'NAME_TAKEN'
  | 'NOT_HOST'
  | 'WRONG_PHASE'
  | 'ILLEGAL_MOVE'
  | 'BAD_INPUT'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'SERVER_RESTARTING';

/** What happened, and what to do about it. */
export const ERROR_TEXT: Record<ErrorCode, string> = {
  ROOM_NOT_FOUND: "That code doesn't match a room. Check it and try again.",
  ROOM_FULL: 'That room already has eight players.',
  GAME_IN_PROGRESS: 'That game has already started. Ask them to start a new one.',
  NAME_TAKEN: 'Someone in the room is already using that name. Pick another.',
  NOT_HOST: 'Only the host can do that.',
  WRONG_PHASE: 'The round moved on. Give it a second.',
  ILLEGAL_MOVE: 'That move is not allowed.',
  BAD_INPUT: "That didn't look right. Try again.",
  RATE_LIMITED: 'Slow down a moment.',
  SERVER_ERROR: 'Something went wrong on our end. Try again.',
  SERVER_RESTARTING: 'The game server is restarting.',
};

/**
 * Application close codes. RFC 6455 §7.4.2 reserves 4000–4999 for exactly this.
 * The client's reconnect policy is keyed on them: 1012 means "back in seconds,
 * retry immediately"; 4001/4003/4004 are terminal and must not be retried.
 */
export const CLOSE = {
  /** IANA 1012 Service Restart — sent to every socket on SIGTERM. */
  SERVICE_RESTART: 1012,
  /** IANA 1013 Try Again Later — capacity. */
  TRY_LATER: 1013,
  /** Host removed this player. Terminal. */
  KICKED: 4001,
  /** The same seat rejoined on a newer socket. This one is a corpse. Terminal. */
  SESSION_REPLACED: 4003,
  /** Socket never sent a valid create/join. Terminal. */
  NEVER_JOINED: 4004,
} as const;

// ── limits, shared so the client can pre-validate and the server can enforce ──

/** The rulebook's own ceiling. */
export const MAX_PLAYERS = 8;
export const MIN_PLAYERS = 2;
export const NAME_MAX = 16;
export const CODE_LENGTH = 4;
/** No I, O, 0 or 1 — these get read aloud and typed by people who are mildly drunk. */
export const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
/**
 * Bytes. Anything larger is rejected without parsing. Larger than needle's 2KB
 * because an `answer` frame can carry a list of chip ids.
 */
export const MAX_MESSAGE_BYTES = 4096;
/** 40 messages per 10 seconds per connection. A draw step is genuinely tappy. */
export const RATE_LIMIT_MESSAGES = 40;
export const RATE_LIMIT_WINDOW_MS = 10_000;

/** Client heartbeat: proves the tab's JS runs AND that its frames reach us. */
export const PING_INTERVAL_MS = 5_000;
/** Server protocol ping. RFC 6455 §5.5.3 makes the browser answer in the stack. */
export const SERVER_PING_MS = 10_000;
/**
 * No inbound frame at all — pong or data — for this long and the socket path is
 * dead. A phone that loses its radio sends no FIN, and Linux would not surface
 * the broken path for ~15 minutes.
 */
export const LIVENESS_TIMEOUT_MS = 25_000;
/**
 * No inbound APPLICATION message for this long and the player is offline, even
 * if the socket still answers protocol pings. These are genuinely different
 * signals: under Chromium's offline emulation the network stack keeps answering
 * `ping` frames after the page has lost connectivity entirely.
 */
export const APP_LIVENESS_MS = 20_000;
/** Offline this long and the host is offered a way to remove them. */
export const HOST_ESCAPE_AFTER_MS = 45_000;
/**
 * Offline this long and the host seat moves. Must exceed APP_LIVENESS_MS:
 * `offlineSince` is backdated to last contact, so anything at or below it would
 * migrate in the same tick that marks the host offline.
 */
export const HOST_MIGRATE_AFTER_MS = 45_000;
/** Idle rooms are wiped after this. Heartbeats do not count as activity. */
export const ROOM_IDLE_MS = 45 * 60_000;
/** A room nobody ever joined is swept far sooner — it is an enumeration target. */
export const EMPTY_ROOM_IDLE_MS = 5 * 60_000;
/** A joiner who hits GAME_IN_PROGRESS retries on this cadence rather than giving up. */
export const REJOIN_RETRY_MS = 10_000;
/**
 * How long a bot waits before acting. Not a limitation — bots decide instantly,
 * and a table where seven opponents move in the same frame is unreadable.
 *
 * Two speeds, because only half of what a bot does is worth watching. Its
 * resolution is the round's drama: it points at somebody, a sword moves, and the
 * log says so. Its draw and play steps are bookkeeping — the chips are facedown
 * and the fist is closed, so there is nothing on screen to read. Pacing those at
 * the same rate means every round opens with twenty seconds of watching seven
 * opponents tap a region.
 */
export const BOT_THINK_MS = 650;
export const BOT_BOOKKEEPING_MS = 130;

/** Which of the two applies, given what the table is doing. */
export function botDelayFor(phase: Phase | 'lobby'): number {
  return phase === 'draw' || phase === 'play' ? BOT_BOOKKEEPING_MS : BOT_THINK_MS;
}

/**
 * Strip control characters, zero-width characters, and bidi overrides, then
 * collapse whitespace. Applied server-side to every name; the client uses it too
 * so what you type is what you see.
 */
export function sanitizeText(input: string): string {
  return input
    // C0 and C1 control characters, including newlines and tabs.
    .replace(/[\u0000-\u001F\u007F-\u009F]/g, ' ')
    // Zero-width space / non-joiner / joiner, bidi marks, embeddings and
    // overrides (U+202A-U+202E and U+2066-U+2069 are the Trojan Source
    // characters, CVE-2021-42574 - in a player list they let one name render
    // as another), word joiner, invisible operators, BOM.
    .replace(
      /[\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u2069\uFEFF]/g,
      '',
    )
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * The key a name collides on. Case-folded and NFKC-normalized, so "emory",
 * "EMORY" and a fullwidth spelling of it are one name at an eight-player table.
 */
export function nameKey(name: string): string {
  return sanitizeText(name).normalize('NFKC').toLowerCase();
}

/** Normalize a typed or pasted share code: uppercase, strip anything not in the alphabet. */
export function normalizeCode(input: string): string {
  return input
    .toUpperCase()
    .split('')
    .filter((ch) => CODE_ALPHABET.includes(ch))
    .join('')
    .slice(0, 6);
}
